import { ShapeBoundsLocator } from './../../editor/diagram/containers/shape-bounds-locator';
import { Stage, Touch, Ticker, MouseEvent, DisplayObject, Container } from '@creately/createjs-module';
import { Observable, fromEvent, merge   } from 'rxjs';
import { filter, map, switchMap, takeUntil, distinctUntilChanged } from 'rxjs/operators';

/**
 * This class extends the createjs Stage and adds new functionality
 * for canvas size and zoom manipulation cosidering the devicePixelRatio property
 * that should account for Retina screens. Call the setup stage to enable all functionality.
 *
 * FIXME
 * The devicePixelRatio ( DPR ) values greater than 1 affect the viewport coordinates.
 * The retina displays have DPR values greater thant 1. Mouse events take the DPR into account
 * but the calculations throughout the application are done based on the event values related to DPR = 1.
 * So this correction should be done from a common place so that it doesn't need to worry about DPR anymore.
 *
 * @author thisun
 * @since 2017-10-17
 */
export class RetinaStage extends Stage {

    /**
     * An observable that emits whenever the screen ratio changes
     * This emits when the window.devicePixelRatio changes.
     */
    public screenRatioChange: Observable<number>;

    /**
     * An observable that emits whenever the screen size changes.
     */
    public screenSizeChange: Observable<any>;

    /**
     * Holds the zoom level and initializes to 1
     */
    protected _zoom: number = 1;
    protected _panX: number = this.x;
    protected _panY: number = this.y;

    constructor( canvas: HTMLCanvasElement | string | Object, protected boundsLocator?: ShapeBoundsLocator ) {
        super( canvas );
        Touch.enable( this, true, true );
    }

    /**
     * The zoom level of the canvas object which applies
     * considering the pixel ratio of the screen
     */
    public set zoom( level: number ) {
        this._zoom = level;
        this.setCanvasScale();
    }
    public get zoom() {
        return this._zoom;
    }

    /**
     * The x offset of the canvas object which considers
     * the pixel ratio of the screen
     */
    public get panX(): number {
        return this._panX;
    }
    public set panX( v: number ) {
        this._panX = v;
        this.setCanvasPan();
    }

    /**
     * The y offset of the canvas object which considers
     * the pixel ratio of the screen
     */
    public get panY(): number {
        return this._panY;
    }
    public set panY( v: number ) {
        this._panY = v;
        this.setCanvasPan();
    }

    /**
     * This function returns the correct mouseX value for the retina satge
     * by deviding the mouseX valuse by window.devicePixelRatio
     */
    public getMouseX() {
        return this.mouseX / window.devicePixelRatio;
    }

    /**
     * This function returns the correct mouseY value for the retina satge
     * by deviding the mouseY valuse by window.devicePixelRatio
     */
    public getMouseY() {
        return this.mouseY / window.devicePixelRatio;
    }

    /**
     * Enables some functionalities of the stage such as scaling the canvsas when the device
     * pixel ratio changes and MONKEY PATHING MOUSE EVENTS WHICH IS A REALLY TERRIBLE IDEA!!!
     * FIXME: some code here should go to the constructor
     * FIXME: add a destroy method and call it where needed.
     */
    public setupStage() {
        // Enable snapToPixelEnabled, increases frame rate
        this.snapToPixelEnabled = true;

        // Setup the screen ratio change observable
        this.screenRatioChange = fromEvent( Ticker, 'tick' )
            .pipe(
                filter(( event: any ) => !event.paused ),
                map(() => window.devicePixelRatio ),
                distinctUntilChanged(),
            );

        // Setup the observable for screen size change
        this.screenSizeChange = this.getScreenSizeChanges();

        // Update the canvas size whenever screen ratio changes or screen size changes
        this.screenSizeChange.subscribe(() => this.updateCanvasSize());

        // Capture all mouse events when screen ratio is not normal and update the
        // event stage values accordingly.
        this.captureMouseEventOnRetinaScreen()
            .subscribe(( event: MouseEvent ) => this.updateStagePosition( event ));
    }

    /**
     * Return an observable which emits whenever the screen ratio or screen size changes
     */
    protected getScreenSizeChanges(): Observable<any> {
        return merge(
            this.screenRatioChange,
            fromEvent( window, 'resize' ),
        );
    }

    /**
     * Returns an observable which emits all mouse events when screen ratio is not normal
     */
    protected captureMouseEventOnRetinaScreen(): Observable<MouseEvent> {
        return this.screenRatioChange
            .pipe(
                filter( ratio => ratio !== 1 ),
                switchMap(() =>
                    merge(
                        fromEvent( this, 'mousedown', { capture: true }),
                        fromEvent( this, 'pressmove', { capture: true }),
                        fromEvent( this, 'pressup', { capture: true }),
                        fromEvent( this, 'stagemousedown' ),
                        fromEvent( this, 'stagemousemove' ),
                        fromEvent( this, 'stagemouseup' ),
                    )
                    .pipe(
                        takeUntil( this.screenRatioChange.pipe( filter( ratio => ratio === 1 )))),
                ),
                map( event => <MouseEvent>event ),
            );
    }

    /**
     * Updates the stage position on the passed event, based on the
     * current window pixel ratio.
     */
    protected updateStagePosition( event: MouseEvent ) {
        event.stageX = event.stageX / window.devicePixelRatio;
        event.stageY = event.stageY / window.devicePixelRatio;
    }

    /**
     * Sets the canvas size to the parent's 100% size.
     * and updates the scaleX and scaleY on changes in zoom level
     * Makes sure the canvas is the viewport size.
     */
    protected updateCanvasSize() {
        const canvasElem: HTMLCanvasElement = <HTMLCanvasElement>this.canvas;
        const widthChanged: boolean = canvasElem.width !== canvasElem.offsetWidth * window.devicePixelRatio;
        const heighChanged: boolean = canvasElem.height !== canvasElem.offsetHeight * window.devicePixelRatio;

        if ( widthChanged ) {
            canvasElem.width = canvasElem.offsetWidth * window.devicePixelRatio;
        }
        if ( heighChanged ) {
            canvasElem.height = canvasElem.offsetHeight * window.devicePixelRatio;
        }
        this.setCanvasScale();
        this.setCanvasPan();
        this.update();
    }

    /**
     * Sets the scale of the canvas based on the pixel ratio
     */
    protected setCanvasScale() {
        this.scaleX = window.devicePixelRatio * this._zoom;
        this.scaleY = window.devicePixelRatio * this._zoom;
    }

    /**
     * Set the pan of the canvas based on the pixel ratio
     */
    protected setCanvasPan() {
        this.x = this._panX * window.devicePixelRatio;
        this.y = this._panY * window.devicePixelRatio;
    }

    /**
     * Overriding the easeljs _getObjectsUnderPoint to optimize it by filtering out the children
     */
    protected _getObjectsUnderPoint( x, y, arr, mouse, activeListener, currentDepth ): any {
        if ( this.boundsLocator ) {
            const p = this.globalToLocal( x, y );
            const shapes = this.boundsLocator.searchShapes( p.x - 10, p.x + 10, p.y - 10, p.y + 10 );
            const children = this.children.filter( c => shapes.find( s => s.id === c.name ));
            return this._getObjectsUnderPointOriginal( x, y, arr, mouse, activeListener, currentDepth, children );
        }
        return this._getObjectsUnderPointOriginal( x, y, arr, mouse, activeListener, currentDepth, this.children );
    }

    // NOTE:
    // This method is copied from easel js _getObjectsUnderPoint and changed to
    // pass children as a param
    /* istanbul ignore next */
    protected _getObjectsUnderPointOriginal( x, y, arr, mouse, activeListener, currentDepth, children ) {
        currentDepth = currentDepth || 0;
        if ( !currentDepth && !( this as any )._testMask( this, x, y )) {
            return null;
        }
        let mtx;
        const ctx = ( DisplayObject as any )._hitTestContext;
        activeListener = activeListener || ( mouse && ( this as any )._hasMouseEventListener());

        // draw children one at a time, and check if we get a hit:
        // const children = this.children;
        const l = children.length;
        for ( let i = l - 1; i >= 0; i-- ) {
            const child = children[i];
            const hitArea = child.hitArea;
            if ( !child.visible || ( !hitArea && !child.isVisible()) || ( mouse && !child.mouseEnabled )) {
                continue;
            }
            if ( !hitArea && !( this as any )._testMask( child, x, y )) {
                continue;
            }

            // if a child container has a hitArea then we only need to check its hitAre2a,
            // so we can treat it as a normal DO:
            if ( !hitArea && child instanceof Container ) {
                const result = ( child as any ).
                    _getObjectsUnderPoint( x, y, arr, mouse, activeListener, currentDepth + 1, children );
                if ( !arr && result ) {
                    return ( mouse && !this.mouseChildren ) ? this : result;
                }
            } else {
                if ( mouse && !activeListener && !( child as any )._hasMouseEventListener()) {
                    continue;
                }
                // TODO: can we pass displayProps forward, to avoid having to calculate
                // this backwards every time? It's kind of a mixed bag. When we're only
                // hunting for DOs with event listeners, it may not make sense.
                const props = child.getConcatenatedDisplayProps(( child as any )._props );
                mtx = props.matrix;

                if ( hitArea ) {
                    mtx.appendMatrix( hitArea.getMatrix(( hitArea as any )._props.matrix ));
                    props.alpha = hitArea.alpha;
                }
                ctx.globalAlpha = props.alpha;
                ctx.setTransform( mtx.a,  mtx.b, mtx.c, mtx.d, mtx.tx - x, mtx.ty - y );
                ( hitArea || child ).draw( ctx );
                if ( !( this as any )._testHit( ctx )) {
                    continue;
                }
                ctx.setTransform( 1, 0, 0, 1, 0, 0 );
                ctx.clearRect( 0, 0, 2, 2 );
                if ( arr ) {
                    arr.push( child );
                } else {
                    return ( mouse && !this.mouseChildren ) ? this : child;
                }
            }
        }
        return null;
    }

}
